import { Avatar, Table, Space, Tag } from 'antd';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getImageUrl, getDataDetail } from 'utils/tools';
import lodashValues from 'lodash/values';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { omit, flatten } from 'lodash';
import { DynamicObjectType } from 'types/commons';
import { useDispatch } from '@redux';
import { getAllProductVariantByProductId } from '@redux/productVariants/actions';
// import ReplaceModal from './ReplaceModal';

interface IProductsOrderProps {
  isRejectReturn: boolean;
  fulfillmentInspection: DynamicObjectType[];
  data: DynamicObjectType;
  items: DynamicObjectType[];
  disableStock?: boolean;
  disableReplace?: boolean;
}

function ProductsOrder({ data: orderData, items }: IProductsOrderProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const stores = useSelector<DynamicObjectType>(
    (state) => state.config.stores,
  ) as DynamicObjectType[];

  const productVariantsData = useSelector<DynamicObjectType>(
    (state) => state.productVariants.data,
  );

  useEffect(() => {
    if (items?.length > 0) {
      dispatch(
        getAllProductVariantByProductId({
          data: {
            filters: [
              {
                condition: 'and',
                key: 'id',
                operator: 'is',
                value: flatten(
                  items?.map((e) => [e, ...(e?.product?.comboItems || [])]),
                )?.map(
                  (e: DynamicObjectType) =>
                    e?.product?.variantId || e.variantId,
                ),
              },
            ],
            // join: ['detail||name,langCode'],
            pageSize: 100,
          },
          options: {
            isRefresh: true,
          },
        }),
      );
    }
  }, [items]);

  const products = useMemo(() => {
    return flatten(
      items?.map((e, itemIndex) => {
        const stock = e?.stocks?.reduce(
          (e, key) => ({
            ...e,
            [key.id]: {
              ...key,
              ...(orderData?.orderType === 'TAB_TRY_BUY'
                ? orderData?.location
                : stores?.find((item) => item.id === key.id)),
            },
          }),
          {},
        );

        return [
          {
            ...e,
            productSortingIndex: itemIndex,
            name: e.productName,
            properties: ['white', 'size-4K'],
            quantity: e.quantity,
            price: Number(e.price),
            currency: 'đ',
            thumbnail:
              e?.variant?.thumbnail ||
              e.productThumbnail ||
              e?.propduct?.thumbnail,
            stocks: lodashValues(stock),
            itemIndex,
            isMatchLotDate: e?.variant?.isMatchLotDate,
            ...(e?.product?.comboItems?.length > 0 && {
              isCombo: true,
            }),
            variant:
              orderData?.items?.find((i) => i?.variantSku === e?.variantSku)
                ?.variant || {},
          },
          ...(e?.product?.comboItems?.length > 0
            ? e?.product?.comboItems?.map((comboItem, comboIndex) => ({
                ...comboItem?.product,
                ...comboItem,
                isMatchLotDate: comboItem?.variant?.isMatchLotDate,
                comboIndex,
                itemIndex,
                quantity: +comboItem?.quantity * +e?.quantity,
                discountedPrice: +comboItem.price / (+comboItem?.quantity || 1),
                comboProduct: {
                  ...omit(e, ['comboItems']),
                  productSortingIndex: itemIndex,
                },
                isComboItem: true,
                name: comboItem.productName,
                currency: 'đ',
                thumbnail:
                  comboItem?.variant?.thumbnail ||
                  comboItem.productThumbnail ||
                  comboItem?.propduct?.thumbnail,
                stocks: lodashValues(
                  comboItem?.stocks?.reduce(
                    (obj, key) => ({
                      ...obj,
                      [key.id]: {
                        ...key,
                        ...(orderData?.orderType === 'TAB_TRY_BUY'
                          ? orderData?.location
                          : stores?.find((item) => item.id === key.id)),
                      },
                    }),
                    {},
                  ),
                ),
              })) || []
            : []),
        ];
      }) || [],
    );
  }, [orderData, stores, productVariantsData]);

  const columns = [
    {
      title: 'Product',
      dataIndex: 'id',
      render: (data, record) => (
        <div
          className={`d-flex ${record?.isComboItem && 'ml-[40px]'} items-center`}
        >
          <Avatar
            size="large"
            shape="square"
            style={{ width: 40, height: 40, marginRight: 20 }}
            src={getImageUrl(record?.thumbnail)}
          />
          <div>
            <Space size={4}>
              {record?.isGift && <Tag color="red">Gift</Tag>}
              <span>{`${record?.isCombo ? 'Combo: ' : ''}${
                record?.variantName ||
                getDataDetail(
                  record,
                  'detail',
                  'name',
                  record?.productName || record?.variant?.slug,
                )?.name
              }`}</span>
              {record?.isComboItem && (
                <Tag color="geekblue" style={{ whiteSpace: 'break-spaces' }}>
                  {`Combo: ${getDataDetail(record?.comboProduct)?.name || getDataDetail(record?.comboProduct?.product)?.name}`}
                </Tag>
              )}
              {record?.bundle && (
                <Tag color="geekblue" style={{ whiteSpace: 'break-spaces' }}>
                  {`Bundle: ${getDataDetail(record?.bundle)?.name}`}
                </Tag>
              )}
            </Space>
            <div className="text-secondary text-sm">
              {record?.variant?.attributeValues?.length > 0 &&
                record?.variant?.attributeValues
                  ?.map((item) => getDataDetail(item)?.name)
                  ?.join('/')}
            </div>
            <div className="description">{`SKU: ${
              record?.variantSku || record?.variant?.sku
            }`}</div>
          </div>
        </div>
      ),
    },
    {
      dataIndex: 'quantity',
      render: (data) => <div className="">{data}</div>,
      title: <div>{t('orderDetail.quantity')}</div>,
    },
  ];

  return (
    <Wrapper className="w-100">
      <div className="order-info-box__content">
        <Table
          pagination={false}
          dataSource={products}
          columns={columns}
          // scroll={{ x: 1024 }}
          rowClassName={(record) => record?.isComboItem && 'combo-item-row'}
        />
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  .combo-item-row {
    td {
      background: #fafafa !important;
    }
  }
  .ant-avatar {
    min-width: 40px;
  }
`;

ProductsOrder.propTypes = {
  data: PropTypes.object,
};

export default ProductsOrder;
